import React, { useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  useTheme,
  Stack
} from '@mui/material';
import {
  Close as CloseIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  WaterDrop as WaterIcon,
  ReceiptLong as ReceiptIcon,
  CalendarMonth as CalendarIcon,
  Person as PersonIcon,
  AccountBalance as BankIcon
} from '@mui/icons-material';

const BillDetailsModal = ({ isOpen, onClose, bill, customer, history = [] }) => {
  const theme = useTheme();
  const printRef = useRef();

  if (!bill || !customer) return null;

  const formatCurrency = (amount) => {
    return `₱${parseFloat(amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatMonth = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'long'
    });
  };

  const handlePrint = () => {
    const printContent = `
      <html>
        <head>
          <title>Water Bill Receipt - ${bill.billid}</title>
          <style>
            body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; padding: 40px; color: #333; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #000; padding-bottom: 20px; }
            .logo-text { font-size: 24px; font-weight: bold; text-transform: uppercase; margin: 0; }
            .sub-text { font-size: 14px; margin: 5px 0; }
            .bill-info { display: flex; justify-content: space-between; margin-bottom: 30px; }
            .section-title { font-size: 16px; font-weight: bold; margin-bottom: 10px; border-bottom: 1px solid #ccc; padding-bottom: 5px; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 20px; }
            .row { display: flex; justify-content: space-between; margin-bottom: 5px; }
            .label { font-weight: bold; color: #555; }
            .amount-box { border: 2px solid #000; padding: 15px; text-align: right; margin-top: 20px; border-radius: 4px; }
            .total-label { font-size: 18px; font-weight: bold; }
            .total-amount { font-size: 24px; font-weight: bold; color: #000; }
            .footer { margin-top: 50px; text-align: center; font-size: 12px; color: #777; border-top: 1px solid #ccc; padding-top: 10px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1 class="logo-text">LGU Concepcion Waterworks</h1>
            <p class="sub-text">Water Billing System</p>
            <p class="sub-text">Official Receipt / Statement of Account</p>
          </div>
          
          <div class="bill-info">
            <div>
              <div class="row"><span class="label">Customer:</span> <span>${customer.name}</span></div>
              <div class="row"><span class="label">Account No:</span> <span>${customer.customerid}</span></div>
              <div class="row"><span class="label">Address:</span> <span>${customer.address || customer.barangay || ''}</span></div>
            </div>
            <div style="text-align: right;">
              <div class="row"><span class="label">Bill No:</span> <span>${bill.billid}</span></div>
              <div class="row"><span class="label">Billing Period:</span> <span>${formatMonth(bill.billedmonth)}</span></div>
              <div class="row"><span class="label">Status:</span> <span>${bill.paymentstatus}</span></div>
            </div>
          </div>

          <div class="grid">
            <div>
              <div class="section-title">Meter Reading Details</div>
              <div class="row"><span class="label">Previous Reading:</span> <span>${bill.previousreading} cu.m</span></div>
              <div class="row"><span class="label">Current Reading:</span> <span>${bill.currentreading} cu.m</span></div>
              <div class="row"><span class="label">Consumption:</span> <span>${bill.consumption} cu.m</span></div>
            </div>
            <div>
              <div class="section-title">Payment Details</div>
              <div class="row"><span class="label">Basic Amount:</span> <span>${formatCurrency(bill.basicamount)}</span></div>
              ${bill.creditApplied > 0 ? `<div class="row"><span class="label" style="color: green;">Credit Applied:</span> <span style="color: green;">-${formatCurrency(bill.creditApplied)}</span></div>` : ''}
              <div class="row"><span class="label">Surcharges/Fees:</span> <span>${formatCurrency(bill.surchargeInfo?.surchargeAmount || bill.surchargeamount)}</span></div>
              <div class="row"><span class="label">Discounts:</span> <span>-${formatCurrency(bill.calculatedDiscountAmount || bill.discountamount)}</span></div>
            </div>
          </div>

          <div class="amount-box">
            <span class="total-label">TOTAL AMOUNT DUE:</span>
            <br/>
            <span class="total-amount">${formatCurrency(bill.calculatedTotal || bill.totalbillamount)}</span>
          </div>

          ${bill.paymentstatus === 'Paid' ? `
            <div style="margin-top: 20px; padding: 10px; background-color: #f0fdf4; border: 1px solid #86efac;">
              <strong>PAYMENT RECEIVED</strong><br/>
              Date: ${formatDate(bill.datepaid)}<br/>
              Processed by: ${bill.paidby || 'System'}
            </div>
          ` : ''}

          <div class="footer">
            <p>This is a system generated document. For inquiries, please contact the municipal waterworks office.</p>
            <p>Printed on ${new Date().toLocaleString()}</p>
          </div>
        </body>
      </html>
    `;
    
    const printWindow = window.open('', '', 'width=800,height=800');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Simple Consumption Graph (Last 6 entries)
  const ConsumptionGraph = () => {
    // Sort history by date ascending and take last 6
    const sortedHistory = [...history, bill]
      .filter((v, i, a) => a.findIndex(t => t.billid === v.billid) === i) // unique
      .sort((a, b) => new Date(a.billedmonth) - new Date(b.billedmonth))
      .slice(-6);

    if (sortedHistory.length < 2) return null;

    const maxConsumption = Math.max(...sortedHistory.map(h => parseFloat(h.consumption || 0)));
    const graphHeight = 150;

    return (
      <Box sx={{ mt: 3, mb: 1 }}>
        <Typography variant="subtitle2" gutterBottom fontWeight="bold">Consumption History</Typography>
        <Box sx={{ 
          height: graphHeight, 
          display: 'flex', 
          alignItems: 'flex-end', 
          justifyContent: 'space-between',
          gap: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 1,
          pt: 2
        }}>
          {sortedHistory.map((item) => {
            const height = maxConsumption > 0 ? (parseFloat(item.consumption || 0) / maxConsumption) * (graphHeight - 20) : 0;
            const isCurrent = item.billid === bill.billid;
            return (
              <Box key={item.billid} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                 <Box 
                  sx={{ 
                    width: '60%', 
                    height: `${Math.max(height, 2)}px`, 
                    bgcolor: isCurrent ? 'primary.main' : 'primary.light',
                    opacity: isCurrent ? 1 : 0.6,
                    borderRadius: '4px 4px 0 0',
                    transition: 'height 0.5s ease',
                    position: 'relative',
                    '&:hover': { opacity: 1 }
                  }}
                >
                  <Typography variant="caption" sx={{ 
                    position: 'absolute', 
                    top: '-20px', 
                    left: '50%', 
                    transform: 'translateX(-50%)',
                    fontWeight: 'bold',
                    fontSize: '0.7rem'
                  }}>
                    {item.consumption}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ mt: 1, fontSize: '0.65rem', textAlign: 'center', lineHeight: 1 }}>
                  {new Date(item.billedmonth).toLocaleDateString('en-PH', { month: 'short', year: '2-digit' })}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  return (
    <Dialog 
      open={isOpen} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        py: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptIcon />
          <Typography variant="h6" fontWeight="bold">Bill Details & Statement</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'inherit' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 3 }}>
        <Grid container spacing={4}>
          {/* Left Column: Details */}
          <Grid item xs={12} md={7}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="overline" color="text.secondary" fontWeight="bold">Customer Information</Typography>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 1, gap: 2 }}>
                <PersonIcon color="action" sx={{ mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">{customer.name}</Typography>
                  <Typography variant="body2" color="text.secondary">ID: {customer.customerid}</Typography>
                  <Typography variant="body2" color="text.secondary">{customer.barangay}</Typography>
                  <Chip 
                    label={customer.type} 
                    size="small" 
                    sx={{ mt: 1 }} 
                    color="info" 
                    variant="outlined" 
                  />
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="overline" color="text.secondary" fontWeight="bold">Meter Reading</Typography>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={4}>
                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                  <Typography variant="caption" display="block" color="text.secondary">Previous</Typography>
                  <Typography variant="h6" fontWeight="bold">{bill.previousreading}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                  <Typography variant="caption" display="block" color="text.secondary">Current</Typography>
                  <Typography variant="h6" fontWeight="bold">{bill.currentreading}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', bgcolor: 'primary.50', borderColor: 'primary.main' }}>
                  <Typography variant="caption" display="block" color="primary.main" fontWeight="bold">Consumption</Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary.main">{bill.consumption} <span style={{fontSize: '0.8rem'}}>m³</span></Typography>
                </Paper>
              </Grid>
            </Grid>

            <ConsumptionGraph />
          </Grid>

          {/* Right Column: Financials */}
          <Grid item xs={12} md={5}>
            <Paper elevation={0} variant="outlined" sx={{ p: 3, height: '100%', bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CalendarIcon color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Billing Month</Typography>
                  <Typography variant="subtitle1" fontWeight="bold">{formatMonth(bill.billedmonth)}</Typography>
                </Box>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Basic Amount</Typography>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" fontWeight="medium">{formatCurrency(bill.basicamount)}</Typography>
                    {bill.creditApplied > 0 && (
                      <>
                        <Typography variant="caption" display="block" color="success.main">
                          - {formatCurrency(bill.creditApplied)} (Credit)
                        </Typography>
                        <Divider sx={{ my: 0.5 }} />
                        <Typography variant="body2" fontWeight="bold">
                           {formatCurrency(parseFloat(bill.basicamount || 0) - parseFloat(bill.creditApplied || 0))}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>
                
                {(bill.surchargeInfo?.surchargeAmount > 0 || bill.surchargeamount > 0) && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', color: 'error.main' }}>
                    <Typography variant="body2">Surcharges / Penalties</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      +{formatCurrency(bill.surchargeInfo?.surchargeAmount || bill.surchargeamount)}
                    </Typography>
                  </Box>
                )}
                
                {(bill.calculatedDiscountAmount > 0 || bill.discountamount > 0) && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', color: 'success.main' }}>
                    <Typography variant="body2">Discount</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      -{formatCurrency(bill.calculatedDiscountAmount || bill.discountamount)}
                    </Typography>
                  </Box>
                )}
                
                <Divider sx={{ borderStyle: 'dashed' }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                  <Typography variant="subtitle1" fontWeight="bold">Total Amount</Typography>
                  <Typography variant="h5" fontWeight="bold" color="primary.main">
                    {formatCurrency(bill.calculatedTotal || bill.totalbillamount)}
                  </Typography>
                </Box>
              </Stack>

              <Box sx={{ mt: 4 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Payment Status</Typography>
                <Chip 
                  label={bill.paymentstatus} 
                  color={bill.paymentstatus === 'Paid' ? 'success' : 'error'} 
                  sx={{ width: '100%', fontWeight: 'bold' }} 
                />
                
                {bill.paymentstatus === 'Paid' && (
                   <Box sx={{ mt: 2, p: 2, bgcolor: 'success.lighter', borderRadius: 1, border: '1px solid', borderColor: 'success.light' }}>
                     <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                       <BankIcon fontSize="small" color="success" />
                       <Typography variant="subtitle2" color="success.dark">Transaction Details</Typography>
                     </Box>
                     <Typography variant="caption" display="block"><strong>Date:</strong> {formatDate(bill.datepaid)}</Typography>
                     <Typography variant="caption" display="block"><strong>Processed By:</strong> {bill.paidby}</Typography>
                   </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Button onClick={onClose} color="inherit">Close</Button>
        <Button onClick={handlePrint} startIcon={<PrintIcon />} variant="outlined" color="primary">
          Print Receipt
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BillDetailsModal;
